import { useState, useEffect, useRef, useCallback } from 'react';
import { useParticipants } from '@livekit/components-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores';
import { getPhaserGameSync } from '../../../PhaserGame';
import Game from '../../../scenes/Game';
import { VideoPosition, VideoElementRefs } from '../types/LiveKitVideoDisplay.types';
import { resolveConferenceMediaEligibility } from '../utils/resolveConferenceMediaEligibility';

const POSITION_UPDATE_THRESHOLD = 3; // pixels - avoid React re-renders for sub-pixel movements

export const useVideoPositioning = () => {
  const participants = useParticipants();
  const [playerPositions, setPlayerPositions] = useState<Map<string, VideoPosition>>(new Map());
  const [expandedVideos, setExpandedVideos] = useState<Set<string>>(new Set());
  const [hoveredVideos, setHoveredVideos] = useState<Set<string>>(new Set());
  
  const positionsRef = useRef<Map<string, VideoPosition>>(new Map());
  const gameRef = useRef<Game | null>(null);
  const videoElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const speakingTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const renderedParticipantsRef = useRef<Set<string>>(new Set());
  const initialPositioningDone = useRef<boolean>(false);

  const isLoading = useSelector((state: RootState) => state.game.loadingGame);
  const virtualRoomFilters = useSelector((state: RootState) => state.virtualRoomFilter.filters);
  const virtualRoomFiltersRef = useRef(virtualRoomFilters);
  virtualRoomFiltersRef.current = virtualRoomFilters;

  // Keep a mutable ref of participants so the scene-update callback always
  // sees the latest list without re-subscribing on every change.
  const participantsRef = useRef(participants);
  participantsRef.current = participants;

  // Initialize game ref once
  useEffect(() => {
    const phaserGame = getPhaserGameSync();
    if (phaserGame) {
      gameRef.current = phaserGame.scene?.keys?.game as Game | undefined;
    }
  }, []);

  // Update position with ref utility function
  const updatePositionWithRef = useCallback((el: HTMLDivElement, position: VideoPosition) => {
    // Set CSS custom properties so that stylesheet rules (like speaking overlays) resolve coordinates correctly
    el.style.setProperty('--x', `${position.x}px`);
    el.style.setProperty('--y', `${position.y}px`);

    // Use translate3d for hardware acceleration
    const transform = `translate3d(${position.x}px, ${position.y}px, 0) translate(-50%, -100%)`;
    if ((el as any).lastTransform !== transform) {
      el.style.transform = transform;
      (el as any).lastTransform = transform;
    }
  }, []);

  // Subscribe to Phaser scene update event instead of running an independent rAF loop.
  // This piggy-backs on the existing Game.update() tick and avoids redundant frame scheduling.
  useEffect(() => {
    // Resolve the scene ref lazily (may not be ready on first render)
    const resolveGame = (): Game | null => {
      if (gameRef.current) return gameRef.current;
      const phaserGame = getPhaserGameSync();
      if (phaserGame) {
        gameRef.current = phaserGame.scene?.keys?.game as Game | undefined ?? null;
      }
      return gameRef.current;
    };

    // Reusable scratch map cleared each frame to avoid per-frame allocation.
    // A new Map is only created when positions actually change (for React state).
    const scratchPositions = new Map<string, VideoPosition>();

    const onSceneUpdate = () => {
      const game = resolveGame();
      if (!game) return;

      const camera = game.cameras.main;
      const cameraX = camera.worldView.x;
      const cameraY = camera.worldView.y;
      const cameraZoom = camera.zoom;

      // Zoom-adaptive offset calculation
      const TARGET_SCREEN_OFFSET_FROM_NAMEPLATE = 70;
      const NAMEPLATE_WORLD_OFFSET = 40;
      const videoWorldOffsetY = NAMEPLATE_WORLD_OFFSET + (TARGET_SCREEN_OFFSET_FROM_NAMEPLATE / cameraZoom);

      scratchPositions.clear();

      // Local player position
      if (game.myPlayer && (game.myPlayer.active || game.myPlayer.visible)) {
        const worldX = game.myPlayer.x;
        const worldY = game.myPlayer.y - videoWorldOffsetY;
        const rx = Math.round((worldX - cameraX) * cameraZoom);
        const ry = Math.round((worldY - cameraY) * cameraZoom);
        scratchPositions.set(game.myPlayer.playerId, { x: rx, y: ry });
      }

      // Remote players
      const currentParticipants = participantsRef.current;
      currentParticipants.forEach((participant) => {
        if (participant.isLocal) return;

        const otherPlayer = game.getOtherPlayerById(participant.identity);
        if (!otherPlayer || (!otherPlayer.active && !otherPlayer.visible)) return;

        if (
          !resolveConferenceMediaEligibility(
            game,
            participant.identity,
            virtualRoomFiltersRef.current,
            { isScreenSharing: participant.isScreenShareEnabled }
          )
        ) {
          return;
        }

        const worldX = otherPlayer.x;
        const worldY = otherPlayer.y - videoWorldOffsetY;
        const rx = Math.round((worldX - cameraX) * cameraZoom);
        const ry = Math.round((worldY - cameraY) * cameraZoom);
        scratchPositions.set(participant.identity, { x: rx, y: ry });
      });

      positionsRef.current = scratchPositions;
      if (scratchPositions.size > 0) initialPositioningDone.current = true;

      // Batch imperative DOM updates (same frame, no extra rAF)
      scratchPositions.forEach((position, id) => {
        if (renderedParticipantsRef.current.has(id)) {
          const element = videoElementsRef.current.get(id);
          if (element && element.isConnected) {
            updatePositionWithRef(element, position);
            // SC-304: restore visibility if previously hidden due to missing sprite
            if (element.style.visibility === 'hidden') {
              element.style.visibility = '';
            }
          } else if (element && !element.isConnected) {
            videoElementsRef.current.delete(id);
          }
        }
      });

      // SC-304: hide video elements whose Phaser sprite no longer exists.
      // Without this, a player whose Colyseus session drops (but LiveKit
      // survives) leaves a "ghost" video tile stuck at its last position.
      renderedParticipantsRef.current.forEach((id) => {
        if (!scratchPositions.has(id)) {
          const element = videoElementsRef.current.get(id);
          if (element && element.isConnected && element.style.visibility !== 'hidden') {
            element.style.visibility = 'hidden';
          }
        }
      });

      // React state only for overlay props / mount; motion uses imperative DOM updates above.
      setPlayerPositions((currentPositions) => {
        // Only trigger state updates (React re-renders) when visible participants enter or leave
        if (scratchPositions.size !== currentPositions.size) {
          return new Map(scratchPositions);
        }
        for (const key of scratchPositions.keys()) {
          if (!currentPositions.has(key)) {
            return new Map(scratchPositions);
          }
        }
        return currentPositions;
      });
    };

    // Subscribe to the Phaser scene 'update' event
    const game = resolveGame();
    if (game) {
      game.events.on('update', onSceneUpdate);
    } else {
      // Scene not ready yet – poll briefly until available, then subscribe
      const interval = setInterval(() => {
        const g = resolveGame();
        if (g) {
          clearInterval(interval);
          g.events.on('update', onSceneUpdate);
        }
      }, 200);
      // Clean up the interval if the component unmounts before the scene is ready
      return () => {
        clearInterval(interval);
        const g = gameRef.current;
        if (g) g.events.off('update', onSceneUpdate);
      };
    }

    return () => {
      const g = gameRef.current;
      if (g) g.events.off('update', onSceneUpdate);
    };
  }, [updatePositionWithRef]);

  // Clean up video element refs when participants change
  useEffect(() => {
    const currentParticipantIds = new Set(
      participants
        .filter((participant) => {
          if (participant.isLocal) return true;
          return true; // We'll filter by proximity in the controller
        })
        .map((participant) => participant.identity)
    );

    // Clean up refs for participants that are no longer rendered
    const elementsToRemove: string[] = [];
    videoElementsRef.current.forEach((element, participantId) => {
      if (!currentParticipantIds.has(participantId)) {
        elementsToRemove.push(participantId);
      }
    });

    elementsToRemove.forEach((participantId) => {
      videoElementsRef.current.delete(participantId);
    });

    // Update the rendered participants ref
    renderedParticipantsRef.current = currentParticipantIds;

    // Clean up speaking timeouts for participants no longer in proximity
    const timeoutsToRemove: string[] = [];
    speakingTimeouts.current.forEach((timeout, participantId) => {
      if (!currentParticipantIds.has(participantId)) {
        clearTimeout(timeout);
        timeoutsToRemove.push(participantId);
      }
    });

    timeoutsToRemove.forEach((participantId) => {
      speakingTimeouts.current.delete(participantId);
    });

    // Clean up expanded/hovered states
    setExpandedVideos((prev) => {
      const filtered = new Set(Array.from(prev).filter((id) => currentParticipantIds.has(id)));
      return filtered.size !== prev.size ? filtered : prev;
    });
    setHoveredVideos((prev) => {
      const filtered = new Set(Array.from(prev).filter((id) => currentParticipantIds.has(id)));
      return filtered.size !== prev.size ? filtered : prev;
    });
    
    // Reset initial positioning flag when participants change
    initialPositioningDone.current = false;
  }, [participants]);

  // Handle video hover events
  const handleVideoHover = useCallback(
    (participantId: string, isHovering: boolean) => {
      if (isHovering) {
        setHoveredVideos((prev) => new Set([...prev, participantId]));
        setExpandedVideos((prev) => new Set([...prev, participantId]));

        // Clear any pending timeout since we're hovering
        const existingTimeout = speakingTimeouts.current.get(participantId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          speakingTimeouts.current.delete(participantId);
        }
      } else {
        setHoveredVideos((prev) => {
          const newSet = new Set(prev);
          newSet.delete(participantId);
          return newSet;
        });
      }
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      speakingTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      speakingTimeouts.current.clear();

      // Clear video element refs
      videoElementsRef.current.clear();
    };
  }, []);

  // Handle speaking state changes with 3-second delay
  const handleSpeakingStateChange = useCallback((speakingIds: Set<string>) => {
    speakingIds.forEach((participantId) => {
      // Immediately expand speaking participants
      if (renderedParticipantsRef.current.has(participantId)) {
        setExpandedVideos((prev) => {
          if (!prev.has(participantId)) {
            return new Set([...prev, participantId]);
          }
          return prev;
        });

        // Clear any existing timeout for this participant since they're speaking
        const existingTimeout = speakingTimeouts.current.get(participantId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          speakingTimeouts.current.delete(participantId);
        }
      }
    });
  }, []);

  // Separate effect to handle shrinking non-speaking videos
  // Bug 16 fix: Track inner timeouts so they can be cleaned up when effect re-runs
  const handleNonSpeakingVideos = useCallback((speakingIds: Set<string>) => {
    // Track timeouts created during this invocation for cleanup
    const createdTimeoutIds: string[] = [];

    // Use a timeout to check for videos that need to be shrunk
    const checkTimeout = setTimeout(() => {
      setExpandedVideos((currentExpanded) => {
        currentExpanded.forEach((participantId) => {
          const isSpeaking = speakingIds.has(participantId);
          const isHovered = hoveredVideos.has(participantId);
          const hasTimeout = speakingTimeouts.current.has(participantId);
          const isRendered = renderedParticipantsRef.current.has(participantId);

          if (!isSpeaking && !isHovered && !hasTimeout && isRendered) {
            // Set timeout to shrink this video
            const timeout = setTimeout(() => {
              setExpandedVideos((prev) => {
                const newSet = new Set(prev);
                newSet.delete(participantId);
                return newSet;
              });
              speakingTimeouts.current.delete(participantId);
            }, 3000);

            speakingTimeouts.current.set(participantId, timeout);
            createdTimeoutIds.push(participantId);
          }
        });

        return currentExpanded;
      });
    }, 100); // Small delay to avoid immediate re-execution

    return () => {
      clearTimeout(checkTimeout);
      // Bug 16: Also clean up inner timeouts created during this invocation
      createdTimeoutIds.forEach((id) => {
        const timeout = speakingTimeouts.current.get(id);
        if (timeout) {
          clearTimeout(timeout);
          speakingTimeouts.current.delete(id);
        }
      });
    };
  }, [hoveredVideos]);

  return {
    playerPositions,
    expandedVideos,
    hoveredVideos,
    videoElementsRef,
    renderedParticipantsRef,
    speakingTimeouts,
    handleVideoHover,
    updatePositionWithRef,
    handleSpeakingStateChange,
    handleNonSpeakingVideos,
  };
}; 